import React, { useState, useEffect } from 'react';
import { Button, Spinner, Alert, ListGroup, Badge } from 'react-bootstrap';
import VoucherService from '../../services/VoucherService/VoucherService';
import CorsiUtentiService from '../../services/CorsiUtentiService/CorsiUtentiService';
import { alertService } from '../../services/AlertService';
import moment from 'moment';

const AssegnaVoucherCorsistaModal = ({ userData, onClose, onSuccess }) => {
  const [vouchers, setVouchers] = useState([]);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAssegnazione, setIsLoadingAssegnazione] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userData?.id_agenzia) {
      loadVouchers();
    } else {
      setError("Il corsista non è associato a nessuna agenzia");
    }
  }, [userData]);

  const loadVouchers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await VoucherService.findVoucherDisponibiliByIdAgenzia(userData.id_agenzia);
      if (response && Array.isArray(response)) {
        setVouchers(response);
        if (response.length === 0) {
          setError("Nessun voucher disponibile per l'agenzia di questo corsista");
        }
      } else {
        setError("Errore nel caricamento dei voucher");
      }
    } catch (error) {
      console.error('Errore nel caricamento dei voucher:', error);
      setError("Errore nel caricamento dei voucher");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssegnaVoucher = async () => {
    if (!selectedVoucher) {
      alertService.warn("Seleziona un voucher", { autoClose: true });
      return;
    }

    setIsLoadingAssegnazione(true);
    try {
      const data = {
        id_utente: userData.id_utente,
        id_utenti_voucher: selectedVoucher.id_utenti_voucher
      };

      const response = await CorsiUtentiService.assegnaVoucherACorsista(data);
      if (response && response.success) {
        alertService.success(`Voucher assegnato con successo! Il corsista è stato iscritto al corso "${response.nome_corso}"`, { autoClose: true });
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        // Estrai il messaggio di errore dalla risposta
        let errorMessage = "Errore durante l'assegnazione del voucher";
        if (response?.error?.message) {
          errorMessage = response.error.message;
        } else if (response?.message) {
          errorMessage = response.message;
        } else if (response?.error) {
          errorMessage = typeof response.error === 'string' ? response.error : JSON.stringify(response.error);
        }
        alertService.error(errorMessage, { autoClose: false });
      }
    } catch (error) {
      console.error('Errore durante l\'assegnazione del voucher:', error);
      // Estrai il messaggio di errore dall'oggetto error
      // ApiService.postRequest passa l'errore direttamente come { error: { code, message, details } }
      let errorMessage = "Errore durante l'assegnazione del voucher";
      
      // Prima verifica la struttura diretta dell'errore (come viene passato da ApiService)
      if (error?.error?.message) {
        errorMessage = error.error.message;
        // Rimuovi il prefisso "Errore di validazione dei campi : " se presente
        if (errorMessage.includes('Errore di validazione dei campi : ')) {
          errorMessage = errorMessage.replace('Errore di validazione dei campi : ', '');
        }
      } 
      // Poi verifica se è una struttura axios-style
      else if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } 
      // Poi verifica se è una struttura alternativa
      else if (error?.response?.data?.error) {
        errorMessage = typeof error.response.data.error === 'string' 
          ? error.response.data.error 
          : (error.response.data.error.message || JSON.stringify(error.response.data.error));
      } 
      // Poi verifica altri formati
      else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } 
      else if (error?.message) {
        errorMessage = error.message;
      }
      // Se non trova nulla, mostra l'errore completo per debug
      else {
        console.error('Struttura errore non riconosciuta:', error);
        errorMessage = "Errore durante l'assegnazione del voucher. Controlla la console per i dettagli.";
      }
      
      alertService.error(errorMessage, { autoClose: false });
    } finally {
      setIsLoadingAssegnazione(false);
    }
  };

  return (
    <div className='row'>
      <div className='col-12 my-3'>
        <span className='small text-muted d-block mb-2'>
          <b>Utente: {userData?.nome} {userData?.cognome}</b>
        </span>
        {userData?.agenzia && (
          <span className='small text-muted d-block mb-3'>
            <b>Agenzia:</b> {userData.agenzia}
          </span>
        )}
        <span className='small text-muted d-block mb-3'>
          Seleziona un voucher formativo disponibile per l'agenzia del corsista
        </span>

        {error && (
          <Alert variant="warning" className="mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Caricamento voucher...</span>
            </Spinner>
          </div>
        ) : vouchers.length > 0 ? (
          <ListGroup className="mb-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {vouchers.map((voucher) => (
              <ListGroup.Item
                key={voucher.id_utenti_voucher}
                action
                active={selectedVoucher?.id_utenti_voucher === voucher.id_utenti_voucher}
                onClick={() => setSelectedVoucher(voucher)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-1">{voucher.nome_corso}</h6>
                    <div className="small text-muted">
                      <div>Quantità disponibile: <strong>{voucher.qt_available}</strong></div>
                      <div>Data scadenza: {moment(voucher.data_scadenza).format('DD/MM/YYYY')}</div>
                      {voucher.modalita_erogazione && (
                        <div>Modalità: {voucher.modalita_erogazione}</div>
                      )}
                    </div>
                  </div>
                  <Badge bg={selectedVoucher?.id_utenti_voucher === voucher.id_utenti_voucher ? 'primary' : 'secondary'}>
                    {selectedVoucher?.id_utenti_voucher === voucher.id_utenti_voucher ? 'Selezionato' : 'Seleziona'}
                  </Badge>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : null}

        <div className='mt-4'>
          {isLoadingAssegnazione ? (
            <Button variant="primary" disabled className="mx-1">
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Assegnazione in corso...
            </Button>
          ) : (
            <Button
              type="button"
              variant='primary'
              onClick={handleAssegnaVoucher}
              disabled={!selectedVoucher || vouchers.length === 0}
              className="mx-1"
            >
              Assegna voucher
            </Button>
          )}
          <button className='btn btn-secondary mx-1' onClick={onClose}>
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssegnaVoucherCorsistaModal;
